import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { isPast } from 'date-fns';

import { Anchor, Box, Button, Heading, Paragraph, Popover, Tag, Text } from '@zillow/constellation-10';

import { PartialMessage } from '@zg-rentals/backend-styled-components';
import { LoadingButton, LoadingText } from '@zg-rentals/loading-components';
import { AdproductsConstants } from '@zg-rentals/rental-revenue-components';
import { useRentalManagerUserPropertyLocationRestrictionsWebApiHandler_restrictions } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useScreenSize } from '../../../hooks';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import useVisibility from '../../../hooks/useVisibility';
import getLocationRestrictionErrorMessage from '../../../utils/getLocationRestrictionErrorMessage';
import { getTagTypeByKey, STATUS_KEY } from '../../../utils/statusTagUtils';
import { getCTAButtonArgs } from '../../ConnectedListingFooter/utils';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';
import PropertyConstants from '../../../constants/PropertyConstants';
import { EXTERNAL_URLS } from '../../../constants/urls';

import DeactivateListingModal from '../../modals/DeactivateListingModal';
import FeedListingDeactivateModal from '../../modals/FeedListingDeactivateModal';
import MultifamilyDeactivationModal from '../../modals/MultifamilyDeactivationModal';
import WaitlistModal from '../../modals/WaitlistModal';
import { AdditionalInformation } from './components/AdditionalInformation';
import LearnMore from './components/LearnMore';

import {
  flexContainerStyles,
  footerContainerStylesMultifamily,
  middleFlexStyles,
  stickyListingFooterContainerStyles,
} from '../../ConnectedListingFooter/styles';

import { useAuth } from '../../AuthProvider';

import type { RefObject } from 'react';

type Props = {
  onPublishClick: (args: { shouldAutoActivate: boolean }) => void;
  isPublishLoading?: boolean;
  isPublishedDisabled?: boolean;
  ctaRef?: RefObject<HTMLButtonElement>;
};

const waitlistEligibleProductTypes = [
  AdproductsConstants.ProductCodes.PAY_PER_LEASE,
  AdproductsConstants.ProductCodes.FLAT_FEE,
];

const Footer = ({ onPublishClick, isPublishLoading = false, isPublishedDisabled = false, ctaRef }: Props) => {
  const router = useRouter();
  const { user } = useAuth();

  const { isScreenMobile } = useScreenSize();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails, isPending: isPendingListingDetails } = useGetListingDetails(listingAlias);
  const { data: addressRestrictions } = useRentalManagerUserPropertyLocationRestrictionsWebApiHandler_restrictions(
    {
      fullAddress: `${listingDetails?.street}, ${listingDetails?.city}, ${listingDetails?.state} ${listingDetails?.zip}`,
      unit: listingDetails?.unit ?? undefined,
      listingType: PropertyConstants.LISTING_CODE_TO_ENUM[listingDetails?.listingType.code as number],
      listingAlias: listingDetails?.listingAlias,
      propertyType: PropertyConstants.PROPERTY_CODE_TO_ENUM[listingDetails?.propertyType.code as number],
    },
    {
      enabled: Boolean(
        listingDetails?.street &&
        listingDetails?.city &&
        listingDetails?.state &&
        listingDetails?.zip &&
        listingDetails?.listingType.code &&
        listingDetails?.propertyType.code &&
        listingDetails?.listingAlias,
      ),
    },
  );

  const {
    adproduct,
    active,
    isActivationBlocked,
    isFeed,
    isPackageProduct,
    isPaidMultifamily,
    isWaitlisted,
    productType,
    selfServeOnboardingStatus,
    syndication,
    freemium,
    rentGuarantee,
    updateActiveStatus,
  } = listingDetails ?? {};

  const { overview: { displayStatus = '' } = {} } = syndication ?? {};
  const { partialMessages = [], isAllowed: isUpdateActiveStatusAllowed } = updateActiveStatus ?? {};

  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [displayDeactivationModal, setDisplayDeactivateModal] = useState(false);
  const [showMultifamilyDeactivationModal, setShowMultifamilyDeactivationModal] = useState(false);
  const [showFeedListingDeactivateModal, setShowFeedListingDeactivateModal] = useState(false);

  const [isVisible, isVisibleRef, recheckVisibility] = useVisibility(-50, 200) as [
    boolean,
    RefObject<HTMLDivElement>,
    () => void,
  ];
  const initialLoadCompletedRef = useRef(false);

  useEffect(() => {
    // Recheck visibility once after initial load completes
    // This handles layout shifts caused by dynamic content loading (banners, stats, etc.)
    if (!initialLoadCompletedRef.current && !isPendingListingDetails && listingDetails) {
      recheckVisibility();
      initialLoadCompletedRef.current = true;
    }
  }, [isPendingListingDetails, listingDetails, recheckVisibility]);

  const locationRestrictionErrorMessage = useMemo(() => {
    return addressRestrictions
      ? getLocationRestrictionErrorMessage(
          { ...addressRestrictions, paidMultifamilyLocation: addressRestrictions.isPaidMultifamily },
          listingDetails?.isMultifamily,
          user?.isMultifamilyCustomer,
          listingDetails?.hasActiveMultifamilyContract,
          user?.isSingleFamilyEnterpriseCustomer,
          user?.isPostingPathAdmin,
        )
      : null;
  }, [
    addressRestrictions,
    listingDetails?.isMultifamily,
    user?.isMultifamilyCustomer,
    listingDetails?.hasActiveMultifamilyContract,
    user?.isSingleFamilyEnterpriseCustomer,
    user?.isPostingPathAdmin,
  ]);

  const isNotActiveAndUpdateActiveStatusNotAllowed = !active && !isUpdateActiveStatusAllowed;
  const isActiveAndUpdateActiveStatusNotAllowed = active && !isUpdateActiveStatusAllowed;
  const isDuplicatePaidListingPosted =
    Boolean(locationRestrictionErrorMessage) && addressRestrictions?.paidListingExists;

  const disablePublishIfDuplicatePaidListingPosted = !isFeed && !active && isDuplicatePaidListingPosted;

  const renderPricingContent = !isActivationBlocked && !disablePublishIfDuplicatePaidListingPosted;

  const isActiveProductExpired =
    freemium?.activeProduct?.expirationTimestamp && isPast(new Date(freemium?.activeProduct?.expirationTimestamp));
  const shouldAutoActivate =
    freemium?.activeProduct?.type === PRODUCT_TYPE_NAME_TO_ID.PREMIUM && !isActiveProductExpired;

  const handleDeactivateClick = () => {
    if (isActiveAndUpdateActiveStatusNotAllowed) {
      // do not fall through. we will display a popper
      return;
    }

    if (isFeed) {
      return setShowFeedListingDeactivateModal(true);
    }

    setDisplayDeactivateModal(true);
  };

  const handleCTAClick = () => {
    if (active) {
      if (
        productType &&
        (isPackageProduct || waitlistEligibleProductTypes.includes(productType) || adproduct?.isUnpaid)
      ) {
        setShowWaitlistModal(true);
      } else if (isPaidMultifamily && !user?.isPostingPathAdmin) {
        setShowMultifamilyDeactivationModal(true);
      } else {
        handleDeactivateClick();
      }
    } else {
      onPublishClick({ shouldAutoActivate });
    }
  };

  const withUnableToDeactivatePopper = (component: JSX.Element) => {
    if (!component) {
      return null;
    }

    return (
      <Popover
        trigger={component}
        tone="info"
        header={<Heading level={6}>Unable to deactivate</Heading>}
        body={
          <Paragraph>
            <PartialMessage partialMessages={partialMessages} />
          </Paragraph>
        }
      />
    );
  };

  const renderButton = ({ textLabel, isButtonDisabled }: { textLabel?: string; isButtonDisabled?: boolean }) => {
    const isCTADeactivate = textLabel?.toLowerCase().includes('deactivate');
    const buttonType = !isCTADeactivate || (isCTADeactivate && isUpdateActiveStatusAllowed) ? 'primary' : 'tertiary';

    return (
      <Button
        ref={ctaRef}
        data-test="StatusButton"
        tone={buttonType === 'primary' ? 'brand' : 'neutral'}
        emphasis={buttonType === 'primary' ? 'filled' : 'outlined'}
        disabled={isButtonDisabled}
        onClick={handleCTAClick}
        loading={isPublishLoading}
        css={{
          alignSelf: 'center',
          height: '44px',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          ...(isButtonDisabled ? { pointerEvents: 'none' } : {}),
        }}
      >
        {textLabel}
      </Button>
    );
  };

  const renderCTAButton = () => {
    if (isPendingListingDetails) {
      return (
        <LoadingButton isLoading={true} loadingWidth="150px">
          &nbsp;
        </LoadingButton>
      );
    }
    const renderedButton = renderButton(
      getCTAButtonArgs({
        active,
        isEligibleForRentGuarantee: rentGuarantee?.isEligible,
        isEnabledForRentGuarantee: rentGuarantee?.isEnabled,
        isFreemiumEnabled: freemium?.isEnabled,
        isNotActiveAndUpdateActiveStatusNotAllowed,
        isPackageProduct,
        isPaidMultifamily,
        isPublishedDisabled,
        isScreenMobile,
        isSelfServeOnboardingInProgress: selfServeOnboardingStatus?.isSelfServeOnboardingInProgress,
        isUpdateRestricted: selfServeOnboardingStatus?.isUpdateRestricted,
        isWaitlisted,
        shouldAutoActivate,
        adproduct,
        features: user?.features,
        productType,
        waitlistEligibleProductTypes,
      }),
    );

    return isActiveAndUpdateActiveStatusNotAllowed ? withUnableToDeactivatePopper(renderedButton) : renderedButton;
  };

  return (
    <>
      <div ref={isVisibleRef} />
      <Box
        data-testid="StickyListingFooterContainer"
        css={stickyListingFooterContainerStyles.raw({
          isVisible: !!isVisible,
        })}
      >
        <Box css={footerContainerStylesMultifamily}>
          <Box css={flexContainerStyles}>
            {isPendingListingDetails ? (
              <LoadingText isLoading={true} loadingWidth="150px">
                &nbsp;
              </LoadingText>
            ) : renderPricingContent ? (
              <>
                <Tag
                  tone={displayStatus ? getTagTypeByKey(STATUS_KEY.LISTING_SUMMARY, displayStatus) : 'gray'}
                  css={{ marginRight: '200', marginBottom: { base: '150', lg: '0' } }}
                >
                  {displayStatus}
                </Tag>
                <Box css={middleFlexStyles}>
                  <LearnMore />
                  <AdditionalInformation />
                </Box>
              </>
            ) : disablePublishIfDuplicatePaidListingPosted && !active ? (
              <Text>
                This property is currently listed from another source. &nbsp;
                <Anchor href={EXTERNAL_URLS.LISTING_DECLINED} target="_blank" rel="noopener noreferrer">
                  Learn more
                </Anchor>
              </Text>
            ) : (
              partialMessages && <PartialMessage partialMessages={partialMessages} />
            )}
          </Box>
          {renderCTAButton()}
        </Box>
      </Box>
      {showWaitlistModal && <WaitlistModal setShowWaitlistModal={setShowWaitlistModal} />}

      {displayDeactivationModal && (
        <DeactivateListingModal
          listingAlias={listingAlias}
          open={displayDeactivationModal}
          onOpenChange={setDisplayDeactivateModal}
        />
      )}

      {showMultifamilyDeactivationModal && (
        <MultifamilyDeactivationModal setShowMultifamilyDeactivateModal={setShowMultifamilyDeactivationModal} />
      )}

      {showFeedListingDeactivateModal && (
        <FeedListingDeactivateModal
          listingAlias={listingAlias}
          open={showFeedListingDeactivateModal}
          onOpenChange={setShowFeedListingDeactivateModal}
        />
      )}
    </>
  );
};

export default Footer;
